import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch, AppThunk } from "@/app/store";
import { fetchInvoicePreview } from "@/api/fetchInvoicePreview/fetchInvoicePreviewApi";
import {
  InvoicePreview,
  FetchInvoicePreviewRequest,
  FetchInvoicePreviewResponse,
} from "@/api/fetchInvoicePreview/types";

interface InvoicePreviewState {
  previews: Record<string, InvoicePreview>; // Keyed by batchReference
  loadingByBatch: Record<string, boolean>; // Track loading per batch
  error: string | null;
}

const initialState: InvoicePreviewState = {
  previews: {},
  loadingByBatch: {},
  error: null,
};

const invoicePreviewSlice = createSlice({
  name: "invoicePreview",
  initialState,
  reducers: {
    setLoadingForBatch: (
      state,
      action: PayloadAction<{ batchReference: string; loading: boolean }>,
    ) => {
      state.loadingByBatch[action.payload.batchReference] =
        action.payload.loading;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setInvoicePreview: (
      state,
      action: PayloadAction<{
        batchReference: string;
        preview: InvoicePreview;
      }>,
    ) => {
      state.previews[action.payload.batchReference] = action.payload.preview;
    },
    clearInvoicePreview: (state, action: PayloadAction<string>) => {
      delete state.previews[action.payload];
      delete state.loadingByBatch[action.payload];
    },
    resetInvoicePreviews: () => initialState,
  },
});

export const {
  setLoadingForBatch,
  setError,
  setInvoicePreview,
  clearInvoicePreview,
  resetInvoicePreviews,
} = invoicePreviewSlice.actions;

export const fetchInvoicePreviewAction =
  (requestData: FetchInvoicePreviewRequest): AppThunk =>
  async (dispatch: AppDispatch) => {
    // Convert batchReference to string for use as a key in state
    const batchReference = String(requestData.batchReference);
    dispatch(setLoadingForBatch({ batchReference, loading: true }));
    dispatch(setError(null));
    try {
      const response: FetchInvoicePreviewResponse =
        await fetchInvoicePreview(requestData);
      dispatch(
        setInvoicePreview({
          batchReference,
          preview: response.data,
        }),
      );
    } catch (error) {
      if (error instanceof Error) {
        dispatch(setError(error.message));
      } else {
        dispatch(setError("Failed to fetch invoice preview"));
      }
    } finally {
      dispatch(setLoadingForBatch({ batchReference, loading: false }));
    }
  };

export default invoicePreviewSlice.reducer;
